import React, { useEffect, useMemo, useState } from 'react';
import { Document, Page, Text, View, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import { toWords } from 'number-to-words';

/**
 * UPDATED (your requested logic)
 * - Header fixed (never overlaps)
 * - Table header repeats every page
 * - ✅ Grand total + remarks + signatures are NOT fixed anymore
 * - ✅ Closing block will NEVER print too low:
 *      - if page doesn't have enough remaining space, it moves to next page
 *      - closing block won't split across pages
 */

try {
  Font.register({
    family: 'BookmanOldStyle',
    fonts: [
      { src: '/bookmanoldstyle.ttf', fontWeight: 'normal', fontStyle: 'normal' },
      { src: '/bookmanoldstyle_italic.ttf', fontWeight: 'normal', fontStyle: 'italic' },
      { src: '/bookmanoldstyle_bold.ttf', fontWeight: 'bold', fontStyle: 'normal' },
      { src: '/bookmanoldstyle_bolditalic.ttf', fontWeight: 'bold', fontStyle: 'italic' },
    ],
  });
} catch (_) {}

/* ============================================================
   Layout constants (pt)
============================================================ */
const TOP_GAP = 95;
const SIDE_GAP = 28;
const HEADER_HEIGHT = 65;

// ✅ keep a small bottom padding (no fixed footer now)
const BOTTOM_GAP = 60;

// ✅ if this much space isn't left, push closing block to next page
const CLOSING_BLOCK_MIN_SPACE = 260;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'BookmanOldStyle',
    fontSize: 8,

    // ✅ reserve ONLY header area now
    paddingTop: TOP_GAP + HEADER_HEIGHT,
    paddingBottom: BOTTOM_GAP,
    paddingLeft: SIDE_GAP,
    paddingRight: SIDE_GAP,

    display: 'flex',
    flexDirection: 'column',
  },

  /* -----------------------------
     Fixed Header
  ----------------------------- */
  header: {
    position: 'absolute',
    top: TOP_GAP,
    left: SIDE_GAP,
    right: SIDE_GAP,
    height: HEADER_HEIGHT,
    justifyContent: 'flex-end',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerText: { fontSize: 10, fontWeight: 'bold' },
  headerTextInvoiceNo: { fontSize: 12, fontWeight: 'bold' },
  invoiceTitle: { fontSize: 12, fontWeight: 'bold', textTransform: 'uppercase' },
  labelBold: { fontWeight: 'bold' },

  section: { fontSize: 9, marginBottom: 8 },
  customerNameLine: {
    marginTop: 2,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    paddingBottom: 3,
    fontWeight: 'bold',
  },

  /* -----------------------------
     Table
  ----------------------------- */
  tableWrapper: { borderWidth: 1, borderColor: '#000000', marginTop: 8 },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    backgroundColor: '#FFFFFF',
  },
  tdRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },

  th: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 8,
    borderRightWidth: 1,
    borderRightColor: '#000000',
    fontWeight: 'bold',
  },
  thLast: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 8,
    fontWeight: 'bold',
  },

  td: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 8,
    borderRightWidth: 1,
    borderRightColor: '#000000',
  },
  tdLast: { paddingVertical: 3, paddingHorizontal: 3, fontSize: 8 },

  textCenter: { textAlign: 'center' },
  textRight: { textAlign: 'right' },

  /* -----------------------------
     Grand total
  ----------------------------- */
  grandTotalWrapper: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000000',
    paddingVertical: 4,
    paddingHorizontal: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    marginTop: 10,
    fontSize: 9,
  },
  grandTotalTitle: { fontSize: 9, fontWeight: 'bold' },
  grandTotalText: { fontSize: 10, fontWeight: 'bold' },
  amountInWords: { fontStyle: 'italic', marginTop: 2, fontSize: 9 },

  /* -----------------------------
     Remarks + Signatures (NORMAL FLOW after totals)
  ----------------------------- */
  closingWrapper: {
    marginTop: 16,
    fontSize: 9,
  },
  remarksLine: {
    marginBottom: 18,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  signaturesContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    margin: '0 auto 0 5rem',
    alignItems: 'flex-end',
  },
  signatureBlock: { width: 153, alignItems: 'center' },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    width: 120,
    height: 14,
    marginBottom: 4,
  },
  signatureLabel: { fontSize: 9, fontWeight: 'bold' },
});

// ---- local utils (no external helpers) ----
const isEmpty = (v) => v === undefined || v === null || String(v).trim() === '';

const safeParseJson = (value, fallback) => {
  if (typeof value !== 'string') return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
};

const isJobRowShape = (value) =>
  Boolean(
    value &&
      typeof value === 'object' &&
      (Object.prototype.hasOwnProperty.call(value, 'job_Id') ||
        Object.prototype.hasOwnProperty.call(value, 'jobCharge') ||
        Object.prototype.hasOwnProperty.call(value, 'job_No') ||
        Object.prototype.hasOwnProperty.call(value, 'doc_No')),
  );

const isChargeRowShape = (value) =>
  Boolean(
    value &&
      typeof value === 'object' &&
      (Object.prototype.hasOwnProperty.call(value, 'job_charge_Id') ||
        Object.prototype.hasOwnProperty.call(value, 'charge_Name') ||
        Object.prototype.hasOwnProperty.call(value, 'charge_Code')),
  );

const money2 = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
};

const convertToWords = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value).trim();
  if (!str) return '';

  const [integerRaw, decimalRaw] = str.split('.');
  const integerPart = Number.isFinite(Number(integerRaw)) ? parseInt(integerRaw, 10) : null;

  const decimalPart =
    decimalRaw && Number.isFinite(Number(decimalRaw)) ? parseInt(decimalRaw.padEnd(2, '0').slice(0, 2), 10) : null;

  let wordsString = 'QAR ';

  if (integerPart !== null) wordsString += toWords(integerPart);
  if (integerPart !== null && decimalPart && decimalPart > 0) wordsString += ' and ';
  if (decimalPart && decimalPart > 0) wordsString += `${toWords(decimalPart)} dirhams`;

  return (
    wordsString
      .trim()
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ') + ' Only.'
  );
};

const normalizeChargeList = (jobCharge) => {
  const parsed = safeParseJson(jobCharge, jobCharge);
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (isChargeRowShape(parsed)) return [parsed];
  if (typeof parsed === 'object') return Object.values(parsed);
  return [];
};

const normalizeList = (value) => {
  const parsed = safeParseJson(value, value);
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (isJobRowShape(parsed)) return [parsed];
  if (typeof parsed === 'object') return Object.values(parsed);
  return [];
};

const isDeletedJobRow = (job) => String(job?.type || '').trim().toLowerCase() === 'delete';

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sumChargeListAmount = (jobCharge) =>
  normalizeChargeList(jobCharge).reduce((sum, c) => {
    const qty = toNum(c?.charge_Qty ?? c?.basis_par);
    if (qty <= 0) return sum;
    const unit = toNum(c?.charge_Unit_Amount ?? c?.unit_price);
    return sum + unit * qty;
  }, 0);

const getChargeLineAmount = (charge = {}) => {
  const qty = toNum(charge?.charge_Qty ?? charge?.basis_par);
  if (qty <= 0) return 0;
  const unit = toNum(charge?.charge_Unit_Amount ?? charge?.unit_price);
  return unit * qty;
};

const sumJobsAmount = (jobs = []) =>
  normalizeList(jobs)
    .filter((job) => !isDeletedJobRow(job))
    .reduce((sum, job) => sum + sumChargeListAmount(job?.jobCharge), 0);

const getFirstNumericValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const trimmed = String(value).trim();
    if (!trimmed || ['null', 'undefined', 'nan'].includes(trimmed.toLowerCase())) continue;

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue;
  }

  return 0;
};

const getSnapshotJobs = (printData) => {
  const invoiceJson = safeParseJson(printData?.invoice_Json, {});
  return normalizeList(invoiceJson?.job_list).filter((job) => job && typeof job === 'object' && !isDeletedJobRow(job));
};

const getPrintJobs = (printData) => {
  const normalizedJobs = normalizeList(printData?.invoice_line_items ?? printData?.invoice_job_map).filter(
    (job) => job && typeof job === 'object' && !isDeletedJobRow(job),
  );

  if (normalizedJobs.length) {
    return normalizedJobs;
  }

  return getSnapshotJobs(printData);
};

const resolveCustomerDisplay = (printData, jobs = []) => {
  const invoiceCustomerId = printData?.customer_Id ?? printData?.customer_id;
  const invoiceCustomerName = String(printData?.customer_Name || '').trim();
  const firstJobWithCustomer = normalizeList(jobs).find((job) => {
    const jobCustomerId = job?.customer_Id ?? job?.customer_id;
    return !isEmpty(job?.customer_Name) || !isEmpty(jobCustomerId);
  });

  const jobCustomerName = String(firstJobWithCustomer?.customer_Name || '').trim();
  return invoiceCustomerName || jobCustomerName || (!isEmpty(invoiceCustomerId) ? `Customer #${invoiceCustomerId}` : '');
};

const getChargeNumericAmount = (item, charge_Code) => {
  const list = normalizeChargeList(item?.jobCharge);
  if (!list.length) return 0;

  // ✅ column code -> which charge_Code(s) should be summed under that column
  const CODE_GROUPS = {
    clearing_charges: ['clearing_charges'],

    // ✅ your data uses this for Customs Duty 450.00
    custom_duty: ['additional_container_clearance', 'custom_duty'],

    handling_charge: ['handling_charge'],
    delivery_order_charges: ['delivery_order_charges'],
  };

  // ✅ "Other" = sum of everything NOT part of the known columns
  if (charge_Code === 'other_charges') {
    const excluded = new Set(Object.values(CODE_GROUPS).flat());

    let sum = 0;
    for (const c of list) {
      const code = c?.charge_Code;
      if (!excluded.has(code)) {
        sum += getChargeLineAmount(c);
      }
    }
    return sum;
  }

  // ✅ if it's a grouped code, sum all matching codes
  if (CODE_GROUPS[charge_Code]) {
    let sum = 0;
    for (const c of list) {
      if (CODE_GROUPS[charge_Code].includes(c?.charge_Code)) {
        sum += getChargeLineAmount(c);
      }
    }
    return sum;
  }

  // fallback: exact match
  const found = list.find((c) => c?.charge_Code === charge_Code);
  return getChargeLineAmount(found);
};

const getChargeAmount = (item, charge_Code) => {
  const amount = getChargeNumericAmount(item, charge_Code);
  return money2(amount);
};

const hasPrintableRow = (item) => {
  if (!item || typeof item !== 'object') return false;
  const chargeCodes = [
    'clearing_charges',
    'custom_duty',
    'handling_charge',
    'delivery_order_charges',
    'other_charges',
  ];

  return chargeCodes.some((code) => Math.abs(getChargeNumericAmount(item, code)) >= 0.0001);
};

const HeaderFixed = ({ printData, invoiceDate }) => (
  <View style={styles.header} fixed>
    <View style={styles.headerRow}>
      <Text style={styles.headerTextInvoiceNo}>
        Invoice No: <Text style={styles.labelBold}>{printData?.invoice_No || ''}</Text>
      </Text>
      <Text style={styles.invoiceTitle}>Invoice</Text>
      <Text style={styles.headerText}>
        Date: <Text style={styles.labelBold}>{invoiceDate}</Text>
      </Text>
    </View>
  </View>
);

// ✅ Closing block (totals + remarks + signatures) that will not print too low
const ClosingBlock = ({ totalAmount, printData }) => (
  <View wrap={false} minPresenceAhead={CLOSING_BLOCK_MIN_SPACE}>
    {/* Grand Total */}
    <View style={styles.grandTotalWrapper}>
      <View>
        <Text style={styles.grandTotalTitle}>Grand Total</Text>
        <Text style={styles.amountInWords}>{convertToWords(totalAmount)}</Text>
      </View>
      <View>
        <Text style={styles.grandTotalText}>{`QAR ${money2(totalAmount)}`}</Text>
      </View>
    </View>

    {/* Remarks + Signatures */}
    <View style={styles.closingWrapper}>
      <View style={styles.remarksLine}>
        <Text>Remarks: </Text>
        <Text style={styles.labelBold}>{printData?.invoice_Remarks || ''}</Text>
      </View>

      <View style={styles.signaturesContainer}>
        <View style={styles.signatureBlock}>
          <View style={styles.signatureLine} />
          <Text style={styles.signatureLabel}>Receiver Signature/Date</Text>
        </View>

        <View style={styles.signatureBlock}>
          <View style={styles.signatureLine} />
          <Text style={styles.signatureLabel}>Accountant</Text>
        </View>
      </View>
    </View>
  </View>
);

const InvoicePdfLoadwiseSingle = ({ printData }) => {
  const [pdfUrl, setPdfUrl] = useState(null);

  const jobList = useMemo(() => getPrintJobs(printData), [printData]);
  const totalAmount = useMemo(
    () => getFirstNumericValue(sumJobsAmount(jobList), printData?.invoice_Summary_TotalAmount, printData?.invoice_Amount),
    [printData, jobList],
  );
  const customerDisplay = useMemo(() => resolveCustomerDisplay(printData, jobList), [printData, jobList]);

  const invoiceDate = useMemo(() => {
    const raw = printData?.invoice_Date;
    if (!raw) return '';
    return String(raw).split(' ')[0];
  }, [printData]);

  const rows = useMemo(() => {
    const activeRows = jobList.filter((row) => row && typeof row === 'object');
    // For multi-job invoices, always show every linked job row even if one job has zero-value charges.
    if (activeRows.length > 1) {
      return activeRows;
    }
    // For single-job invoices, keep old behavior and hide fully-empty rows.
    return activeRows.filter((row) => hasPrintableRow(row));
  }, [jobList]);

  useEffect(() => {
    let objectUrl;

    const build = async () => {
      const Doc = (
        <Document>
          <Page size="A4" style={styles.page} wrap>
            {/* ✅ Fixed Header */}
            <HeaderFixed printData={printData} invoiceDate={invoiceDate} />

            {/* Main content */}
            <View>
              <View style={styles.section}>
                <Text>Customer Name:</Text>
                <Text style={styles.customerNameLine}>{customerDisplay}</Text>
              </View>

              <View style={styles.tableWrapper}>
                {/* ✅ Repeat table header every page */}
                <View style={styles.tableHeaderRow} fixed>
                  <Text style={[styles.th, { width: '6%' }, styles.textCenter]}>SNo</Text>
                  <Text style={[styles.th, { width: '12%' }, styles.textCenter]}>Doc No</Text>
                  <Text style={[styles.th, { width: '12%' }, styles.textCenter]}>Load No</Text>

                  <Text style={[styles.th, { width: '14%' }, styles.textRight]}>Clearing</Text>
                  <Text style={[styles.th, { width: '14%' }, styles.textRight]}>Customs Duty</Text>
                  <Text style={[styles.th, { width: '14%' }, styles.textRight]}>Handling</Text>
                  <Text style={[styles.th, { width: '14%' }, styles.textRight]}>Delivery</Text>

                  <Text style={[styles.thLast, { width: '14%' }, styles.textRight]}>Other</Text>
                </View>

                {/* Rows */}
                {rows.map((item, idx) => {
                  const empty = !item;
                  const clearingAmount = empty ? '' : getChargeAmount(item, 'clearing_charges');
                  const customsDutyAmount = empty ? '' : getChargeAmount(item, 'custom_duty');
                  const handlingAmount = empty ? '' : getChargeAmount(item, 'handling_charge');
                  const deliveryAmount = empty ? '' : getChargeAmount(item, 'delivery_order_charges');
                  const otherAmount = empty ? '' : getChargeAmount(item, 'other_charges');

                  return (
                    <View style={styles.tdRow} key={idx} wrap>
                      <Text style={[styles.td, { width: '6%' }, styles.textCenter]}>{empty ? '' : idx + 1}</Text>

                      <Text style={[styles.td, { width: '12%' }, styles.textCenter]}>
                        {empty ? '' : item?.doc_No || ''}
                      </Text>

                      <Text style={[styles.td, { width: '12%' }, styles.textCenter]}>
                        {empty ? '' : String(toNum(item?.job_Load_No))}
                      </Text>

                      <Text style={[styles.td, { width: '14%' }, styles.textRight]}>
                        {clearingAmount ? `QAR ${clearingAmount}` : ''}
                      </Text>

                      <Text style={[styles.td, { width: '14%' }, styles.textRight]}>
                        {customsDutyAmount ? `QAR ${customsDutyAmount}` : ''}
                      </Text>

                      <Text style={[styles.td, { width: '14%' }, styles.textRight]}>
                        {handlingAmount ? `QAR ${handlingAmount}` : ''}
                      </Text>

                      <Text style={[styles.td, { width: '14%' }, styles.textRight]}>
                        {deliveryAmount ? `QAR ${deliveryAmount}` : ''}
                      </Text>

                      <Text style={[styles.tdLast, { width: '14%' }, styles.textRight]}>
                        {otherAmount ? `QAR ${otherAmount}` : ''}
                      </Text>
                    </View>
                  );
                })}
              </View>

              {/* ✅ Closing block: never prints too low; moves to next page if needed */}
              <ClosingBlock totalAmount={totalAmount} printData={printData} />
            </View>
          </Page>
        </Document>
      );

      const blob = await pdf(Doc).toBlob();
      objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl + '#zoom=90%');
    };

    build();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [printData, invoiceDate, rows, totalAmount, customerDisplay]);

  if (!pdfUrl) return <div>Generating invoice…</div>;

  return (
    <div style={{ height: '85vh', width: '100%' }}>
      <iframe src={pdfUrl} title="Invoice Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
    </div>
  );
};

export default InvoicePdfLoadwiseSingle;
